/**
 * EPA NAAQS designations by county — a lookup over the bundled EPA Green Book
 * NAYRO table (`src/data/epaOzoneNonAttainment.json`, generated by
 * `scripts/data-ingest/ingestEpaGreenBookNayro.ts`; provenance in the sibling
 * `.meta.json`). County identity comes from the tiger-counties refdata
 * snapshot, with geo.fcc.gov as the live fallback.
 *
 * Site-level containment (EPA-7): for a CURRENT NONATTAINMENT row that covers
 * only PART of the county, EPA's nonattainment-area GIS boundaries are queried
 * at the site point (`epaNonattainmentBoundaries.ts`) and the row carries
 * `siteContainment` ('inside' | 'outside' | 'unknown'). Counties without such
 * a row stay zero-network. The lookup never changes a county-level field; a
 * failed lookup reads 'unknown', which consumers treat exactly as the
 * county-level answer before this change.
 *
 * ABSENCE IS NEVER ATTAINMENT. Every result carries `coverage` (the table
 * vintage). A county missing from the national table is `designationStatus:
 * 'not-listed'` — no current nonattainment or maintenance designation listed
 * as of the EPA export date — which is narrower than an attainment
 * determination. A result WITHOUT `coverage` predates the national table (the
 * one-row stub) and is not an assessment at all. Consumers read results via
 * `readEpaDesignation` / `describeEpaDesignation` in `src/lib/sed/epaDesignation.ts`.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { fetchWithTimeout } from './types';
import { resolveFipsFromRefdata } from '../refdata/fipsLookup';
import { resolvePartCountyContainment } from './epaNonattainmentBoundaries';
import {
  EPA_NONATTAINMENT_AREAS_SERVICE_URL,
  EPA_NONATTAINMENT_BOUNDARIES_FIELD,
  EPA_NONATTAINMENT_BOUNDARIES_SOURCE,
} from '../../lib/epaNonattainmentBoundarySource';
import epaNonattainmentTable from '../../data/epaOzoneNonAttainment.json';
import epaNonattainmentTableMeta from '../../data/epaOzoneNonAttainment.meta.json';

/** Criteria-pollutant group of a Green Book standard label. */
export type NaaqsPollutant = 'ozone' | 'pm2.5' | 'pm10' | 'so2' | 'no2' | 'co' | 'lead';

/** Current status of a county within one NAAQS area. Revoked standards are excluded upstream. */
export type NaaqsDesignationStatus = 'nonattainment' | 'maintenance';

/** Whether EPA's nonattainment-area boundary places the site point inside a part-county area. */
export type NaaqsSiteContainmentStatus = 'inside' | 'outside' | 'unknown';

/**
 * Site-point containment in a part-county nonattainment area, from EPA's
 * nonattainment-area GIS boundaries (`epaNonattainmentBoundaries.ts`). A
 * screen at the site query point, not a boundary survey: 40 CFR Part 81 is the
 * legal boundary.
 */
export interface NaaqsSiteContainment {
  status: NaaqsSiteContainmentStatus;
  /**
   * Why the status is 'unknown' (stable category, never error text):
   * 'boundary-lookup-failed' — the lookup failed, was cancelled, or returned a
   *   malformed or truncated answer (retried on the next build);
   * 'boundary-area-not-identified' — the layer holds no polygon with the Green
   *   Book area's name and state, so no answer can be tied to this area;
   * 'withheld-private-acceptance' — a private refdata acceptance run, which
   *   reproduces from frozen sources only.
   */
  reason?: 'boundary-lookup-failed' | 'boundary-area-not-identified' | 'withheld-private-acceptance';
  /** The EPA MapServer layer queried. */
  layerUrl?: string;
}

/** One county × standard × area row from EPA Green Book NAYRO. */
export interface NaaqsDesignation {
  /** Standard label exactly as EPA Green Book spells it, e.g. "8-Hour Ozone (2015)", "PM-2.5 (2012)". */
  naaqs: string;
  pollutant: NaaqsPollutant;
  status: NaaqsDesignationStatus;
  /**
   * Area classification ("Marginal" … "Extreme" for ozone, "Moderate" /
   * "Serious" for particulates). Absent for standards without classifications
   * (SO2 2010, Lead). For a maintenance area, the classification at redesignation.
   */
  classification?: string;
  /** Green Book nonattainment or maintenance area name. */
  area: string;
  /** Whether the whole county, or only part of it, lies in the area. */
  extent: 'whole' | 'part';
  /** Multi-state area in which only some states have been redesignated. */
  splitArea?: true;
  /** Effective date (ISO) of the state's redesignation to attainment; present on maintenance rows. */
  redesignatedOn?: string;
  /**
   * Current nonattainment rows with `extent: 'part'` only: whether EPA's
   * nonattainment-area boundary places the site point inside this area.
   * Absent on every other row, on rows of a standard EPA publishes no
   * boundary layer for (Sulfur Dioxide (1971), Lead (1978)), and on results
   * produced before EPA-7; absent reads as 'unknown'.
   */
  siteContainment?: NaaqsSiteContainment;
}

/** One county (or Connecticut planning region) in `src/data/epaOzoneNonAttainment.json`. */
export interface EpaNonattainmentCountyEntry {
  county: string;
  state: string;
  /** Standards with a CURRENT nonattainment designation (maintenance excluded), sorted. */
  pollutants: string[];
  /** Most stringent current 8-hour ozone nonattainment classification, when any. */
  classification?: string;
  /**
   * Connecticut planning regions (Census 2022+ county-equivalents) only: the
   * legacy county FIPS whose designations the entry combines. NAYRO keys
   * Connecticut by legacy county; the county resolver returns planning regions.
   */
  derivedFrom?: string[];
  /** Every current designation, nonattainment first, then maintenance. */
  designations: NaaqsDesignation[];
}

/** 5-digit FIPS → entry. Counties absent from the key set are not listed by EPA. */
export type EpaNonattainmentLookup = Record<string, EpaNonattainmentCountyEntry>;

/** Provenance header for the bundled table (`src/data/epaOzoneNonAttainment.meta.json`). */
export interface EpaNonattainmentMeta {
  /** Coverage id stamped on every provider result, e.g. 'green-book-nayro-2026-08-31'. */
  coverage: string;
  /** NAYRO `ExportDt` (ISO) — the data vintage. */
  exportDate: string;
  source: {
    publisher: string;
    downloadPage: string;
    url: string;
    fileName: string;
    dataDictionaryUrl: string;
    sha256: string;
    bytes: number;
    httpLastModified: string | null;
    downloadedOn: string;
  };
  ctPlanningRegionCrosswalk: {
    url: string;
    sha256: string;
    httpLastModified: string;
  };
  generator: string;
  rowCounts: { total: number; revokedExcluded: number; nonattainment: number; maintenance: number; duplicatesCollapsed: number };
  entryCounts: { total: number; withNonattainment: number; maintenanceOnly: number; ctPlanningRegions: number };
  naaqsIncluded: string[];
  revokedNaaqsExcluded: string[];
  notes: string[];
}

/** County listing in the national table. */
export type EpaDesignationStatus = 'nonattainment' | 'maintenance' | 'not-listed';

export interface EpaAttainmentData {
  fips: string;            // 5-digit county FIPS e.g. "22019"
  county: string;          // e.g. "Calcasieu Parish"
  state: string;           // e.g. "LA"
  /**
   * True when any part of the county lies in a CURRENT nonattainment area for
   * any NAAQS. County-level: a designation with extent 'part' does not
   * establish that the site itself is inside the area (see its
   * `siteContainment`), and this flag never follows that containment.
   */
  isNonAttainment: boolean;
  /** Standards with a current nonattainment designation, e.g. ["8-Hour Ozone (2015)", "PM-2.5 (2012)"]. */
  pollutants: string[];
  /**
   * Most stringent current 8-hour ozone nonattainment classification:
   * "Marginal" | "Marginal (Rural Transport)" | "Moderate" | "Serious" |
   * "Severe 15" | "Severe 17" | "Extreme". Other pollutants' classifications
   * are on `designations`.
   */
  classification?: string;
  dataSource: 'epa-nayro';
  /**
   * Table the answer came from, e.g. 'green-book-nayro-2026-08-31'. Absent on
   * results produced before the national table (the one-row stub): those are
   * NOT ASSESSED and must never be read as attainment.
   */
  coverage?: string;
  /**
   * 'not-listed' = no current nonattainment or maintenance designation for any
   * NAAQS listed as of the export date. Standards EPA has not yet designated
   * are not covered, so this is not an attainment determination.
   * Absent together with `coverage` on legacy results.
   */
  designationStatus?: EpaDesignationStatus;
  /** Every current designation for the county; revoked standards excluded. */
  designations?: NaaqsDesignation[];
}

// The build script is the schema authority for both JSON files; the
// data-contract tests in scripts/data-ingest/__tests__ pin the shape.
const TABLE = epaNonattainmentTable as unknown as EpaNonattainmentLookup;
const META = epaNonattainmentTableMeta as unknown as EpaNonattainmentMeta;

/** Coverage id of the bundled table, e.g. 'green-book-nayro-2026-08-31'. */
export const EPA_NAYRO_COVERAGE: string = META.coverage;
/** EPA export date (ISO) of the bundled table. */
export const EPA_NAYRO_EXPORT_DATE: string = META.exportDate;

const GREEN_BOOK_DOWNLOAD_PAGE = 'https://www.epa.gov/green-book/green-book-data-download';

/** Headline listing for a county's designations (`undefined` = not in the table). */
export function designationStatusOf(entry: EpaNonattainmentCountyEntry | undefined): EpaDesignationStatus {
  const designations = entry?.designations ?? [];
  if (designations.some(d => d.status === 'nonattainment')) return 'nonattainment';
  return designations.length > 0 ? 'maintenance' : 'not-listed';
}

export const epaAttainmentProvider: DataProvider<EpaAttainmentData> = {
  id: 'epa-attainment',
  name: 'EPA NAAQS Designations (Green Book NAYRO)',
  cacheTtlMs: 2592000000, // 30 days fallback since CACHE_TTL.REGULATORY does not exist
  requiresKey: false,
  // The table vintage partitions the cache: a regenerated table must never be
  // answered from results computed on an older one (the pre-2026-09 stub said
  // "not nonattainment" for every county except Los Angeles). Cells are
  // 4-decimal (~11 m), not the shared 2-decimal grid (~1 km): the answer is a
  // point-in-county lookup, so a coarse cell could hand a site the county and
  // designation of a neighbour across a county line. The lookup is local, so
  // the finer key costs next to nothing. `gis1` partitions results carrying
  // part-county site containment (EPA-7) from the county-only results cached
  // before it, which would otherwise be served for up to 30 days.
  cacheKey: ({ lat, lng }) => `${EPA_NAYRO_COVERAGE}:gis1:${lat.toFixed(4)}_${lng.toFixed(4)}`,

  async fetch(
    lat: number,
    lng: number,
    _apiKey?: string,
    geo?: ProviderGeoContext,
  ): Promise<ProviderResult<EpaAttainmentData>> {
    // lat/lng is the boundary centroid when a polygon is drawn
    // (`epa-attainment` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. The county, and so the designation, follows the parcel: a
    // mailbox geocode can sit across a county line from the parcel it serves.
    // RD-1 (C5) — local-first county/state from the tiger-counties snapshot;
    // geo.fcc.gov stays as the live fallback.
    let fips: string;
    let county: string;
    let state: string;
    let fipsSource: string;
    const localFips = await resolveFipsFromRefdata(lat, lng);
    if (localFips) {
      fips = localFips.countyFips;
      county = localFips.countyName;
      state = localFips.stateCode;
      fipsSource = 'Census TIGER county boundaries';
    } else {
      const url = `https://geo.fcc.gov/api/census/block/find?format=json&latitude=${lat}&longitude=${lng}`;
      const res = await fetchWithTimeout(url, 15_000, geo?.signal);

      if (!res.ok) {
        throw new Error(`FCC Census Block API failed with HTTP ${res.status}`);
      }

      const json = await res.json();
      if (!json?.Block?.FIPS) {
        throw new Error('FCC Census Block API returned no FIPS data');
      }

      fips = json.Block.FIPS.slice(0, 5);
      county = json.County?.name ?? 'Unknown County';
      state = json.State?.code ?? 'Unknown State';
      fipsSource = 'FCC Census Block API';
    }

    const entry: EpaNonattainmentCountyEntry | undefined = TABLE[fips];
    const designationStatus = designationStatusOf(entry);
    // EPA-7: part-county nonattainment rows are resolved at the same point the
    // county came from. Never throws; a failure leaves them 'unknown'.
    const containment = await resolvePartCountyContainment(entry?.designations ?? [], state, lat, lng, geo?.signal);

    const data: EpaAttainmentData = {
      fips,
      county,
      state,
      isNonAttainment: designationStatus === 'nonattainment',
      pollutants: entry ? [...entry.pollutants] : [],
      dataSource: 'epa-nayro',
      coverage: EPA_NAYRO_COVERAGE,
      designationStatus,
      designations: containment.designations,
    };

    if (entry?.classification) {
      data.classification = entry.classification;
    }

    return {
      data,
      sources: [{
        field: 'epaAttainment',
        source: `EPA Green Book NAYRO county designations (export ${EPA_NAYRO_EXPORT_DATE}); county via ${fipsSource}`,
        url: GREEN_BOOK_DOWNLOAD_PAGE,
        retrievedAt: new Date().toISOString(),
        dataAsOf: EPA_NAYRO_EXPORT_DATE,
      },
      // Cited only when the boundary lookup resolved a row: a lookup that
      // answered nothing is never a source.
      ...(containment.resolvedAt ? [{
        field: EPA_NONATTAINMENT_BOUNDARIES_FIELD,
        source: EPA_NONATTAINMENT_BOUNDARIES_SOURCE,
        url: EPA_NONATTAINMENT_AREAS_SERVICE_URL,
        retrievedAt: containment.resolvedAt,
      }] : [])],
      // A failed or withheld boundary lookup is retried on the next build
      // instead of standing for the 30-day TTL.
      ...(containment.retryable ? { cacheTtlMs: 0 } : {}),
    };
  },
};
